<template>
  <div id="main-last-samples" :class="lastSamplesLayoutClass">
    <!-- Title and icon -->
    <div class="section-header">
      <div class="section-icon">
        <!-- Samples Icon -->
        <img
          :src="samplesIconSvg"
          style="
            width: 17px;
            height: 25px;
            display: block;
            object-fit: contain;
          "
          alt="icon"
        />
      </div>
      <div class="section-title">
        <h2>{{ $t('lastSamples.title') }}</h2>
      </div>
      <div>
        <a
          id="dashboard-info"
          class="dashboard-info"
          @mouseenter="toggleWithAnimationOptions"
          @mouseleave="toggleWithAnimationOptions"
        >
          <i class="f7-icons f7-icon-custom">info_circle_fill</i>
        </a>
      </div>
    </div>
    <div class="section-content">
      <template v-if="samplesWithDataCount">
        <GenericSampleTypeCard
          v-for="(sampleType, index) in samplesWithData"
          :key="index"
          :sample-type="sampleType"
        >
          <template #icon>
            <img
              :src="getSampleTypeIcon(sampleType)"
              class="sample-type-icon"
              alt="icon"
            />
          </template>
        </GenericSampleTypeCard>
      </template>

      <LastSamplesEmptyState
        v-else
        :last-refresh-at="lastRefreshAt"
      />
    </div>
    <DxPopover
      width="auto"
      height="auto"
      class="dashboard-popover"
      :visible="withAnimationOptionsVisible"
      :animation="animationConfig"
      target="#dashboard-info"
      position="right"
    >
      <div style="padding: 1px 15px">
        <p>{{ $t('lastSixMonthsInfo') }}</p>
      </div>
    </DxPopover>
  </div>
</template>

<script>
import { DxPopover } from 'devextreme-vue/popover';
import GenericSampleTypeCard from '@/components/Dashboard/LastSamples/GenericSampleTypeCard.vue';
import LastSamplesEmptyState from '@/components/Dashboard/LastSamples/LastSamplesEmptyState.vue';
// Iconos SVG
import samplesIconSvg from '@/static/icons/muestra.svg';
import agricultureSvg from '@/static/icons/agricultura-2.svg';
import alimentariaSvg from '@/static/icons/alimentaria.svg';
import ambientalSvg from '@/static/icons/ambiental.svg';
import mineriaSvg from '@/static/icons/mineria.svg';
import anotacionesLabSvg from '@/static/icons/anotaciones_lab.svg';

export default {
  name: 'Home',
  components: {
    GenericSampleTypeCard,
    LastSamplesEmptyState,
    DxPopover,
  },
  props: {
    availableSamples: { type: Array, default: () => [] },
    lastRefreshAt: {
      type: [Date, String, Number],
      default: null,
    },
  },
  data() {
    return {
      withAnimationOptionsVisible: false,
      animationConfig: {
        show: {
          type: 'pop',
          from: {
            scale: 0,
          },
          to: {
            scale: 1,
          },
        },
        hide: {
          type: 'fade',
          from: 1,
          to: 0,
        },
      },
    };
  },
  computed: {
    samplesIconSvg: () => samplesIconSvg,
    // Filtro para unicamente mostrar las muestras que tienen datos
    samplesWithData() {
      return this.availableSamples.filter((sample) => {
        const sampleData = Object.values(sample)[0];
        return (
          sampleData.in_process > 0 ||
          sampleData.in_alert > 0 ||
          sampleData.pending_review > 0
        );
      });
    },
    samplesWithDataCount() {
      return this.samplesWithData.length;
    },
    lastSamplesLayoutClass() {
      return {
        'last-samples--empty': this.samplesWithDataCount === 0,
        'last-samples--single': this.samplesWithDataCount === 1,
        'last-samples--two': this.samplesWithDataCount === 2,
        'last-samples--many': this.samplesWithDataCount > 2,
      };
    },
  },
  methods: {
    toggleWithAnimationOptions() {
      this.withAnimationOptionsVisible =
        !this.withAnimationOptionsVisible;
    },
    getSampleTypeIcon(sampleType) {
      const sampleTypeKey = Object.keys(sampleType)[0];

      switch (sampleTypeKey) {
        case 'Agronomía':
          return agricultureSvg;
        case 'Alimentaria':
          return alimentariaSvg;
        case 'Medio Ambiente':
          return ambientalSvg;
        case 'Minería':
          return mineriaSvg;
        case 'Salud y Seguridad':
          return anotacionesLabSvg;
        default:
          return agricultureSvg;
      }
    },
  },
};
</script>

<style lang="scss">
// Por ahora se comenta el card de solicitud de materiales, y se adapta para que ambos cards ocupen un 80% centrados y uno sobre otro
#main-last-samples {
  /* Layout */
  display: flex;
  padding: 24px 28px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
  margin: 0 auto;
  height: 100%;
  min-height: var(--dashboard-feature-card-min-height, 210px);
  box-sizing: border-box;
  /* Style */
  border-radius: 10px;
  background: var(--Basic-white, white);
  box-shadow: 0 1px 4px 0 rgba(87, 84, 95, 0.3);

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    .section-title h2 {
      font-weight: 500;
      font-size: 18px;
      line-height: 120%;
      color: var(--Primary-Blue-60, #04509f);
      margin: 0;
    }
  }

  .section-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 1 auto;
    gap: 20px;
    justify-content: flex-start;
    min-height: 0;
    align-self: stretch;
  }

  &.last-samples--single,
  &.last-samples--two {
    .section-content {
      justify-content: center;
    }
  }

  &.last-samples--single {
    .generic-sample-card {
      flex: 1 1 auto;
      min-height: 112px;
    }
  }

  &.last-samples--two {
    .section-content {
      gap: 14px;
    }

    .generic-sample-card {
      min-height: 82px;
    }
  }

  &.last-samples--empty {
    .section-content {
      justify-content: center;
      align-items: stretch;
    }
  }
}

/* Tamaño de iconos svg */
@media (min-width: 1601px) {
  #main-last-samples {
    // En pantallas amplias este bloque gana un poco más de aire
    // para acompañar la escala del resto del dashboard.
    padding: 32px;
    gap: 32px;

    .section-content {
      gap: 20px;
    }
  }
}

#main-last-samples .generic-sample-icon {
  img.sample-type-icon {
    width: 28px;
    height: 28px;
    display: block;
    object-fit: contain;
  }
}

#main-last-samples .section-header .section-title h2 {
  font-size: 17px !important;
}

@media (max-width: 1600px) and (min-width: 1401px) {
  #main-last-samples:not(.last-samples--empty) {
    .generic-sample-card {
      gap: 12px;
      padding: 7px 10px;

      .generic-sample-card-header {
        width: 200px;
        gap: 10px;
      }

      .generic-sample-icon {
        width: 30px;
        height: 30px;

        img.sample-type-icon {
          width: 24px;
          height: 24px;
        }
      }

      .generic-sample-card-title .sample-title-section {
        h3 {
          font-size: 15px;
        }

        p {
          font-size: 13px;
        }
      }

      .generic-sample-card-status {
        gap: 10px;
      }

      .sample-details-card {
        gap: 9px;
        min-height: 40px;
        padding: 6px 9px 6px 16px;
      }

      .sample-details-card.sample-alerts .icon-alert,
      .sample-details-card.sample-not-reviewed .icon-not_reviewed,
      .sample-details-card.sample-in-process .icon-in_process {
        width: 28px;
        height: 28px;

        img {
          width: 20px;
          height: 20px;
        }
      }

      .sample-alert-content,
      .sample-not-reviewed-content,
      .sample-in-process-content {
        gap: 6px;

        h4 {
          font-size: 19px;
        }

        p {
          font-size: 14px;
        }
      }

      .sample-card-arrow {
        font-size: 15px;
      }
    }
  }
}

.dashboard-grid-item-lead #main-last-samples {
  padding: 22px;
  gap: 18px;

  .section-content {
    gap: 16px;
  }

  &.last-samples--two {
    .section-content {
      gap: 12px;
    }
  }
}

@media (max-width: 1400px) {
   #main-last-samples:not(.last-samples--empty) {
    .generic-sample-card {
      gap: 12px;
      padding: 7px 10px;

      .generic-sample-card-header {
        width: 165px;
        gap: 10px;
      }

      .generic-sample-icon {
        width: 30px;
        height: 30px;

        img.sample-type-icon {
          width: 22px;
          height: 22px;
        }
      }

      .generic-sample-card-title .sample-title-section {
        h3 {
          font-size: 15px;
        }

        p {
          font-size: 13px;
        }
      }

      .generic-sample-card-status {
        gap: 10px;
      }

      .sample-details-card {
        gap: 9px;
        min-height: 40px;
        padding: 6px 9px 6px 16px;
      }

      .sample-details-card.sample-alerts .icon-alert,
      .sample-details-card.sample-not-reviewed .icon-not_reviewed,
      .sample-details-card.sample-in-process .icon-in_process {
        width: 24px;
        height: 24px;

        img {
          width: 14px;
          height: 14px;
        }
      }

      .sample-alert-content,
      .sample-not-reviewed-content,
      .sample-in-process-content {
        gap: 6px;

        h4 {
          font-size: 17px;
        }

        p {
          font-size: 13px;
        }
      }

      .sample-card-arrow {
        display:none;
      }
    }
  }
}

.dashboard-grid-item-lead #main-last-samples {
  padding: 22px;
  gap: 18px;

  .section-content {
    gap: 16px;
  }

  &.last-samples--two {
    .section-content {
      gap: 12px;
    }
  }
}

@media (max-width: 768px) {
  #main-last-samples {
    padding: 18px;
    gap: 18px;
  }
}

.dashboard-popover {
  display: none;
}
</style>
